// Libs
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';

// Components
import AdminModal from '../AdminModal';
import { Button, Flexbox, SearchableSingleSelect } from '@ibinder/common-frontend';
import { Toggle } from '@ibinder/ui-common-core';

// Queries
import { GET_ALL_ORGS, GET_ORG_ID, MOVE_BINDER_TO_ORG } from '../../../../../../api/queries/queries_local';

const ModalWrapper = styled.div`
  padding: 1rem;
  height: 100%;
  overflow-x: auto;
`;
const Header = styled.p`
  margin-top: 1rem;
  margin-bottom: 1rem;
  span {
    font-weight: 700;
  }
`;
const BottomActionBar = styled(Flexbox)`
  margin-top: auto;
  box-shadow: ${(p) => p.theme.shadows.primaryWithOpacity};
  padding: 1rem;
`;
const ErrorMessage = styled.p`
  margin: 1rem 0;
  color: ${(p) => p.theme.colors.error};
  font-weight: 700;
`;
const InformationBox = styled.div`
  padding: 0.5rem 0;
  margin: 1rem 0;
  color: ${(p) => p.theme.colors.success};
  span {
    font-weight: 700;
  }
`;

const DomainList = styled.div`
  margin: 1rem 0;
  label {
    margin-bottom: 0.5rem;
  }
`;

// Same domain names and order as AdminApi OrganizationAdminService.MoveBinderToTenant runs them
const DOMAINS = [
  { key: 'binders', label: 'Binders' },
  { key: 'users', label: 'Users' },
  { key: 'bookshelves', label: 'Bookshelves' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'conversations', label: 'Conversations' },
  { key: 'issues', label: 'Issues (not verified)' },
  { key: 'contracts', label: 'Contracts (not verified)' },
  { key: 'inspections', label: 'Inspections (not verified)' },
  { key: 'documents', label: 'Documents' }
];

const ChangeOrganizationModal = ({
  onClose,
  data,
  selectedIds = [],
  orgId
}) => {

  const [selectedValue, setSelectedValue] = useState(null);
  const [activeIndex, setActiveIndex] = useState(undefined);
  const [moveBinderIsLoading, setMoveBinderIsLoading] = useState(undefined);
  const [isError, setIsError] = useState(undefined);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [ignoreExceptionIfMoveFilesFails, setIgnoreExceptionIfMoveFilesFails] = useState(true);
  // Source org is locked after the first run, since the binder may already belong to the new org
  const [sourceOrganization, setSourceOrganization] = useState(null);
  const [movedDomains, setMovedDomains] = useState<string[]>([]);
  const [lastRunDomains, setLastRunDomains] = useState<string[]>([]);

  const getAllOrgs = useQuery(GET_ALL_ORGS);
  
  const getOrg = useQuery(GET_ORG_ID, {
    variables: { id: orgId }
  })

  const organizationData = getAllOrgs.data && getAllOrgs.data.get; 
  const currentOrganization = sourceOrganization ?? getOrg.data?.getOrg;
  const binderType = data.find((d) => d.binder.id === selectedIds[0]).binder.type;
  const theNewOrg = organizationData?.find((e) => e.id === selectedValue)?.name

  const [moveBinderToOrg] = useMutation(MOVE_BINDER_TO_ORG, {
    onCompleted: () => {
      setMoveBinderIsLoading(false);
      setMovedDomains((prev) => [...prev, ...lastRunDomains.filter((d) => !prev.includes(d))]);
      setSelectedDomains([]);
    },
    onError: () => {
      setIsError(true);
      setMoveBinderIsLoading(undefined);
    }
  });

  const resetForm = () => {
    setSelectedValue(null);
    setMoveBinderIsLoading(undefined);
  };
  const onCloseModal = () => {
    onClose();
    resetForm();
  };

  const getNewOrganization = (index) => {
    setSelectedValue(index)
    setActiveIndex(index)
  }

  const toggleDomain = (domain: string, checked: boolean) => {
    setSelectedDomains((prev) =>
      checked ? [...prev, domain] : prev.filter((d) => d !== domain)
    );
  };

  const changeOrganization = () => {
    setSourceOrganization(currentOrganization);
    setLastRunDomains(selectedDomains);
    setIsError(undefined);
    setMoveBinderIsLoading(true);
    moveBinderToOrg(
      {
        variables: {
          body: {
            tenantId: currentOrganization?.id,
            binderId: selectedIds[0],
            newTenantId: selectedValue,
            // Always send domains explicitly, otherwise the backend runs every domain in one request
            domains: selectedDomains,
            ignoreExceptionIfMoveFilesFails
          }
        }
      }
    )
  }

  return (
    <AdminModal
      onClose={onCloseModal}
      label="Change organization"
      name="Change organization"
      Component={() => (
        <>
        <ModalWrapper>
         <Header>This binder is connected to organization 
           <span>
            {' '}{currentOrganization?.name}
           </span>
          </Header>

          {binderType === "Organization" && 
            <ErrorMessage>You can not move the binder if the type is Organization</ErrorMessage>
          }
          {isError && 
            <ErrorMessage>Something went wrong! Please contact 2ndline.</ErrorMessage>
          }
          {moveBinderIsLoading &&
            <InformationBox>
              <p>
                <span>Moving the binder..</span>
              </p>
            </InformationBox>
            }
            {moveBinderIsLoading === false &&
              <InformationBox>
                <p>Success! <span>{lastRunDomains.join(', ')}</span> moved from <span>{currentOrganization?.name}</span> to <span>{theNewOrg}.</span></p>
              </InformationBox>
            }
            {movedDomains.length > 0 &&
              <Header>Moved so far: <span>{movedDomains.join(', ')}</span></Header>
            }

          {sourceOrganization ? (
            <Header>Moving to organization <span>{theNewOrg}</span></Header>
          ) : (
            <SearchableSingleSelect
              label="Select the organization to move the binder to."
              activeKey={activeIndex}
              loading={organizationData === undefined}
              dropdownText="Select organization"
              changeActiveItem={(index) => getNewOrganization(index)}
              data={organizationData?.map((o) => ({
                Value: o.name,
                Key: o.id
              }))}
            />
          )}

          <Header>Select which parts to move. Run them one at a time to make troubleshooting easier.</Header>
          <DomainList>
            {DOMAINS.map((domain) => (
              <Toggle
                key={domain.key}
                label={movedDomains.includes(domain.key) ? `${domain.label} (moved)` : domain.label}
                toggled={selectedDomains.includes(domain.key)}
                disabled={moveBinderIsLoading === true}
                onChange={(e) => toggleDomain(domain.key, e.target.checked)}
              />
            ))}
          </DomainList>
          <Toggle
            label="Ignore exception if move files fails"
            toggled={ignoreExceptionIfMoveFilesFails}
            disabled={moveBinderIsLoading === true}
            onChange={(e) => setIgnoreExceptionIfMoveFilesFails(e.target.checked)}
          />

        </ModalWrapper>
        <BottomActionBar justifyContent="space-between">
          <Button
            onClick={onClose}
            label="Close"
            btnStyle="secondary"
            btnExtras={['labelWidth']}
          />
          <Button
            label="Move selected parts"
            onClick={changeOrganization}
            btnStyle="primary"
            btnExtras={['labelWidth']}
            disabled={!selectedValue || selectedDomains.length === 0 || moveBinderIsLoading === true || binderType === "Organization"}
          />
        </BottomActionBar>
        </>
      )}
    />
  );
};

export default ChangeOrganizationModal;
